// App.js

import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Breadcrumb, message, Space, Tooltip, Dropdown, Badge, ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import 'antd/dist/reset.css';
import {
  HomeOutlined, FileOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ToolOutlined,
  TableOutlined,
  LogoutOutlined,
  UsergroupAddOutlined,
  DownOutlined,
  KeyOutlined,
  DeleteRowOutlined,
  ApartmentOutlined,
  MessageOutlined,
  BellOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider, useBrand } from './context/BrandContext';
import { buildAntdTheme } from './theme';
import ProtectedRoute from './routes/ProtectedRoute';
import LoginPage from './components/auth/LoginPage';
import BrandMark from './components/common/BrandMark';

// Importación de componentes de página
import HomePage from './components/HomePage';
import GlobalSearchModal from './components/GlobalSearchModal';
import DeliverySearch from './components/DeliverySearch';
import PackingListEditor from './components/PackingListEditor';
import PackingListEditorPFS from './components/PackingListEditorPFS'; 
import MobilePhotoCapture from './mobile/MobilePhotoCapture';
import PalletCatalogPage from './components/admin/PalletCatalogPage';
import UserManagementPage from './components/admin/UserManagementPage';
import MyAccountPage from './components/account/MyAccountPage';
import DeletionRequestsPage from './components/DeletionRequestsPage'; 
import ClientProductCodesPage from './components/admin/ClientProductCodesPage';
import DeletionReasonCatalogPage from './components/admin/DeletionReasonCatalogPage';
import CaptureSuccessPage from './components/CaptureSuccessPage';
import SettingsPage from './components/admin/SettingsPage';
import ProntoPagoPage from './components/cobranza/ProntoPagoPage';
import InactivityDetector from './components/auth/InactivityDetector';
import './App.css';

const { Header, Content, Footer, Sider } = Layout;

const PAGE_TITLES = [
  { test: (p) => p.startsWith('/administracion/usuarios'), title: 'Gestión de usuarios' },
  { test: (p) => p.startsWith('/administracion/catalogos/tarimas'), title: 'Catálogo · Tarimas' },
  { test: (p) => p.startsWith('/administracion/catalogos/client-codes'), title: 'Catálogo · Códigos de cliente' },
  { test: (p) => p.startsWith('/administracion/catalogos/motivos-eliminacion'), title: 'Catálogo · Motivos de eliminación' },
  { test: (p) => p.startsWith('/administracion/configuracion'), title: 'Configuración' },
  { test: (p) => p.startsWith('/packing-list-pfs'), title: 'Packing List · Editor (PFS)' },
  { test: (p) => p.startsWith('/packing-list'), title: 'Packing List · Editor' },
  { test: (p) => p.startsWith('/deliveries'), title: 'Entregas' },
  { test: (p) => p.startsWith('/cobranza/pronto-pago'), title: 'Bonificación por pronto pago' },
  { test: (p) => p.startsWith('/solicitudes-eliminacion'), title: 'Solicitudes de eliminación' },
  { test: (p) => p.startsWith('/my-account'), title: 'Mi cuenta' },
  { test: (p) => p === '/', title: 'Panel principal' },
];

const getPageTitle = (path) => {
  const match = PAGE_TITLES.find(p => p.test(path));
  return match ? match.title : 'Sistema de Gestión Optimus';
};

const getInitials = (user) => {
  if (!user) return '·';
  if (user.fullName) {
    const parts = user.fullName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (user.username || '··').slice(0, 2).toUpperCase();
};

const PackingListEditorWrapper = ({ isPFS }) => {
  const { docEntryParam } = useParams(); 
  const navigate = useNavigate();
  const location = useLocation();
  
  const isReadOnly = location.state?.readOnly || false;

  const handleBack = () => {
    const returnPath = location.state?.from || '/deliveries';
    let navigationState = {}; 
    if (location.state?.activeTabKey) {
      navigationState = { 
        activeTab: location.state.activeTabKey 
      };
    }
    navigate(returnPath, { state: navigationState });
  };

  return isPFS 
    ? <PackingListEditorPFS docEntry={docEntryParam} onBack={handleBack} isReadOnly={isReadOnly} />
    : <PackingListEditor docEntry={docEntryParam} onBack={handleBack} isReadOnly={isReadOnly} />;
};

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout, pendingCount } = useAuth();
  const { brand } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const initials = getInitials(user);

  const onCollapse = (collapsedStatus) => {
    setCollapsed(collapsedStatus);
  };

  // Atajo global Ctrl+K (o Cmd+K en Mac) abre la búsqueda. Esc la cierra.
  useEffect(() => {
    const onKeydown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, []);

  const handleSelectDelivery = (selectedRecord) => {
    if (!selectedRecord || selectedRecord.DocEntry === undefined) {
        message.error("Error al seleccionar la entrega.");
        navigate('/deliveries');
        return;
    }
    // El formato del listado ya no se decide por UDF de la entrega.
    // Todos los listados nuevos van al editor estándar; el campo tipoCliente
    // (nacional/exportacion) viaja en el record y será usado por el editor para
    // condicionar campos del flujo de exportación cuando se implementen.
    navigate(`/packing-list/${selectedRecord.DocEntry}`);
  };
  
  const getCurrentSectionForMenu = () => {
    const path = location.pathname;
    if (path.startsWith('/administracion/usuarios')) return 'usuarios-permisos';
    if (path.startsWith('/administracion/catalogos/tarimas')) return 'catalogo-tarimas';
    if (path.startsWith('/administracion/catalogos/client-codes')) return 'catalogo-codigos-cliente';
    if (path.startsWith('/administracion/catalogos/motivos-eliminacion')) return 'catalogo-motivos-eliminacion';
    if (path.startsWith('/administracion/configuracion')) return 'configuracion-general';
    if (path.startsWith('/packing-list-pfs') || path.startsWith('/packing-list') || path.startsWith('/deliveries')) return 'deliveries';
    if (path.startsWith('/cobranza/pronto-pago')) return 'pronto-pago';
    if (path.startsWith('/solicitudes-eliminacion')) return 'solicitudes-eliminacion';
    if (path.startsWith('/my-account')) return '';
    if (path === '/') return 'home';
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0) return segments[0]; 
    return 'home'; 
  };
  
  const currentMenuKey = getCurrentSectionForMenu();

  const getDefaultOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/administracion/usuarios')) return ['administracion'];
    if (path.startsWith('/administracion/catalogos')) return ['administracion', 'catalogos'];
    if (path.startsWith('/administracion')) return ['administracion'];
    if (path.startsWith('/cobranza')) return ['cobranza'];
    return [];
  };

  const userMenu = {
    items: [
      { key: '/my-account', icon: <KeyOutlined />, label: 'Mi Cuenta' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar Sesión', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') {
        logout();
      } else {
        navigate(key);
      }
    },
  };

  const menuItems = useMemo(() => {
    const permissions = user?.role?.profile?.permissions?.pages;
    const items = [];

    items.push({ key: 'home', icon: <HomeOutlined />, label: <Link to="/">Inicio</Link> });
    
    if (permissions?.viewDeliveries) {
        items.push({ key: 'deliveries', icon: <FileOutlined />, label: <Link to="/deliveries">Entregas</Link> });
    }

    if (permissions?.viewDeletionManagement) {
        // El Badge ya no está aquí, se ha movido al Header.
        items.push({ 
            key: 'solicitudes-eliminacion', 
            icon: <DeleteRowOutlined />, 
            label: <Link to="/solicitudes-eliminacion">Gestión Eliminación</Link>
        });
    }
    
    // Cobranza › Pronto pago: solo Tecno (gate de marca) y con permiso explícito.
    // El backend además blinda el endpoint con req.tenant.key === 'tecno'.
    if (permissions?.viewProntoPago && brand.key === 'tecno') {
        items.push({
            key: 'cobranza',
            icon: <DollarOutlined />,
            label: 'Cobranza',
            children: [
                { key: 'pronto-pago', label: <Link to="/cobranza/pronto-pago">Pronto pago</Link> },
            ],
        });
    }

    if (permissions?.viewAdminPanel) {
        const adminChildren = [];
        if (permissions.viewAdminUsers) {
            adminChildren.push({ 
                key: 'usuarios-permisos', 
                icon: <UsergroupAddOutlined />, 
                label: <Link to="/administracion/usuarios">Gestión Acceso</Link>
            });
        }
        if (permissions.viewAdminCatalogs) {
            const catalogChildren = [];
            if (permissions.viewAdminPallets) {
                catalogChildren.push({ key: 'catalogo-tarimas', icon: <TableOutlined />, label: <Link to="/administracion/catalogos/tarimas">Tarimas</Link> });
            }
            if(permissions.viewClientCodes) {
                 catalogChildren.push({ key: 'catalogo-codigos-cliente', icon: <ApartmentOutlined />, label: <Link to="/administracion/catalogos/client-codes">Artículos</Link> });
            }
            if (permissions.viewAdminDeletionReasons) {
                catalogChildren.push({ key: 'catalogo-motivos-eliminacion', icon: <MessageOutlined />, label: <Link to="/administracion/catalogos/motivos-eliminacion">Motivos Elim</Link> });
            }
            if(catalogChildren.length > 0){
                 adminChildren.push({ key: 'catalogos', icon: <DatabaseOutlined />, label: 'Catálogos', children: catalogChildren });
            }
        }
        if (permissions.viewAdminConfig) {
            adminChildren.push({ 
                key: 'configuracion-general', 
                icon: <ToolOutlined />, 
                label: <Link to="/administracion/configuracion">Configuración</Link>
            });
        }

        if (adminChildren.length > 0) {
            items.push({ key: 'administracion', icon: <SettingOutlined />, label: 'Administración', children: adminChildren });
        }
    }

    return items;
  }, [user, brand.key]);


  const generateBreadcrumbItems = () => {
    const path = location.pathname;
    const items = [
      { key: 'home', title: <Link to="/"><HomeOutlined /> Inicio</Link> },
    ];
    if (path.startsWith('/deliveries')) { items.push({ key: 'deliveries', title: <Link to="/deliveries">Entregas</Link> }); }
    if (path.startsWith('/packing-list/') || path.startsWith('/packing-list-pfs/')) { if (!path.startsWith('/deliveries')) { items.push({ key: 'deliveries', title: <Link to="/deliveries">Entregas</Link> }); } items.push({ key: 'packing-list-editor', title: 'Listado de Empaque' }); }
    if (path.startsWith('/cobranza/pronto-pago')) { items.push({ key: 'cobranza', title: 'Cobranza' }); items.push({ key: 'pronto-pago', title: 'Pronto pago' }); }
    if (path.startsWith('/solicitudes-eliminacion')) { items.push({ key: 'solicitudes-eliminacion', title: 'Gestión de Eliminaciones' }); }
    if (path.startsWith('/my-account')) { items.push({ key: 'my-account', title: 'Mi Cuenta' }); }
    if (path.startsWith('/administracion')) {
      items.push({ key: 'administracion', title: 'Administración' });
      if (path.startsWith('/administracion/usuarios')) {
        items.push({ key: 'usuarios', title: 'Gestión de Acceso' });
      }
      if (path.startsWith('/administracion/catalogos')) {
        items.push({ key: 'catalogos', title: 'Catálogos' });
        if (path.startsWith('/administracion/catalogos/tarimas')) {
          items.push({ key: 'tarimas', title: 'Tarimas' });
        }
        if (path.startsWith('/administracion/catalogos/client-codes')) {
            items.push({ key: 'client-codes', title: 'Códigos de Artículo por Cliente' });
        }
        if (path.startsWith('/administracion/catalogos/motivos-eliminacion')) {
            items.push({ key: 'motivos-eliminacion', title: 'Motivos de Eliminación' });
        }
      } else if (path.startsWith('/administracion/configuracion')) {
        items.push({ key: 'configuracion', title: 'Configuración' });
      }
    }
    if (path.startsWith('/historial-empaques')) { items.push({ key: 'historial-empaques', title: 'Historial Empaques' }); }
    return items;
  };

  const breadcrumbItems = generateBreadcrumbItems();

  return (
    <Layout style={{ minHeight: '100vh' }} className="app-layout">
      <InactivityDetector />
      <Sider collapsible collapsed={collapsed} onCollapse={onCollapse} className="app-sider" width={220}>
        <div className="sider-brand">
          <BrandMark size={28} dark />
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[currentMenuKey]}
          defaultOpenKeys={getDefaultOpenKeys()}
          items={menuItems}
        />
        <div className="sider-foot">v2.4.1 · SAP B1 sync</div>
      </Sider>
      <Layout className="site-layout">
        <Header className="app-header">
          <div className="app-page-title">{pageTitle}</div>
          <div style={{ flexGrow: 1 }} />
          <div
            className="app-header-search"
            onClick={() => setSearchOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearchOpen(true); } }}
            title="Buscar (Ctrl+K)"
          >
            <span className="app-header-search-icon">⌕</span>
            <span className="app-header-search-text">Buscar entrega, cliente…</span>
            <span className="app-header-search-kbd">Ctrl K</span>
          </div>
          {user && (
            <Space size={10}>
              {user?.role?.profile?.permissions?.pages?.viewDeletionManagement && (
                <Tooltip title="Ver solicitudes de eliminación pendientes">
                  <span
                    onClick={() => navigate('/solicitudes-eliminacion')}
                    className="app-header-bell"
                  >
                    <Badge count={pendingCount} color={brand.accent} offset={[-2, 4]} title="">
                      <BellOutlined style={{ fontSize: 16 }} />
                    </Badge>
                  </span>
                </Tooltip>
              )}

              <Dropdown menu={userMenu} trigger={['click']}>
                <div className="app-user-pill">
                  <span className="app-user-avatar" style={{ background: brand.primary }}>
                    {initials}
                  </span>
                  <span className="app-user-name">{user.username}</span>
                  <DownOutlined style={{ fontSize: 9, color: '#5e6779' }} />
                </div>
              </Dropdown>
            </Space>
          )}
        </Header>
        <Content className="app-content">
          <Breadcrumb style={{ marginBottom: '16px' }} items={breadcrumbItems} />
          <div className="site-layout-background" style={{ padding: 24, minHeight: 360, background: '#fff' }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/deliveries" element={<DeliverySearch onSelectDelivery={handleSelectDelivery} />} />
              
              <Route path="/packing-list/:docEntryParam" element={<PackingListEditorWrapper isPFS={false} />} />
              <Route path="/packing-list-pfs/:docEntryParam" element={<PackingListEditorWrapper isPFS={true} />} />
              
              <Route path="/cobranza/pronto-pago" element={<ProntoPagoPage />} />

              <Route path="/solicitudes-eliminacion" element={<DeletionRequestsPage />} />
              
              <Route path="/my-account" element={<MyAccountPage />} />
              
              <Route path="/administracion/usuarios" element={<UserManagementPage />} />
              <Route path="/administracion/catalogos/tarimas" element={<PalletCatalogPage />} />
              <Route path="/administracion/catalogos/client-codes" element={<ClientProductCodesPage />} />
              <Route path="/administracion/catalogos/motivos-eliminacion" element={<DeletionReasonCatalogPage />} />
              <Route path="/administracion/configuracion" element={<SettingsPage />} />
              
            </Routes>
          </div>
        </Content>
        <Footer className="app-footer">
          Sistema de Gestión Optimus  © {new Date().getFullYear()} - Integrado con SAP Business One
         </Footer>
      </Layout>
      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Layout>
  );
};

const ThemedApp = () => {
  const { brandKey } = useBrand();
  const theme = useMemo(() => buildAntdTheme(brandKey, false), [brandKey]);

  return (
    <ConfigProvider locale={esES} theme={theme}>
      <Router>
        <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/mobile-capture/:sessionId" element={<MobilePhotoCapture />} />
              <Route path="/capture-success" element={<CaptureSuccessPage />} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              } />
            </Routes>
        </AuthProvider>
      </Router>
    </ConfigProvider>
  );
};

const App = () => (
  <BrandProvider>
    <ThemedApp />
  </BrandProvider>
);

export default App;
